import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DataTable from "../../Table/DataTable";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import {
  commercialAccount,
  deleteCommercial,
  fetchCommercials,
} from "../../../REDUX/actions/commercials";
import { usePartnerOrganizations } from "../usePartnerOrganizations";
import CommercialModal from "./CommercialModal";

// Commercials (a store's sales reps) — Strapi envelope rows (row.attributes.*),
// scoped server-side (reseller = own store; admin = all). Picked as the
// "Commercial" of a project / order. "Access" = its own SOFT login (userRole
// 7): created here, it then sees its own customers and orders, no prices.
const CommercialsTable = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { commercials, loading, pageCount, total, deleteLoading } = useSelector(
    (state) => state.commercialsReducer
  );
  // A reseller only sees its own store: no store column.
  const { canPickOrganization } = usePartnerOrganizations();

  const fetchData = (page, search, _filters, signal) => {
    dispatch(fetchCommercials(search, page, signal));
  };

  const fullName = (row) =>
    [row?.attributes?.firstName, row?.attributes?.lastName]
      .filter(Boolean)
      .join(" ");
  const accountOf = (row) => row?.attributes?.user?.data ?? null;
  const accessLabel = (row) => {
    const account = accountOf(row);
    if (!account) return t("No access");
    return account.attributes?.blocked ? t("Disabled") : t("Active");
  };
  const account = (row, action) => dispatch(commercialAccount(row.id, action, t));

  const headers = [
    "Last name",
    "First name",
    "Phone number",
    "Email",
    ...(canPickOrganization ? ["Reseller"] : []),
    "Access",
    "",
  ];
  const columns = [
    { name: "Last name", render: (row) => row?.attributes?.lastName ?? "-" },
    { name: "First name", render: (row) => row?.attributes?.firstName ?? "-" },
    {
      name: "Phone number",
      render: (row) => row?.attributes?.phoneNumber ?? "-",
    },
    { name: "Email", render: (row) => row?.attributes?.email || "-" },
    ...(canPickOrganization
      ? [
          {
            name: "Reseller",
            render: (row) =>
              row?.attributes?.organization?.data?.attributes?.name ?? "-",
          },
        ]
      : []),
    { name: "Access", render: accessLabel },
    {
      name: "options",
      render: (row) => (
        <VerticalPoints
          showLeft
          options={[
            {
              text: t("Edit"),
              callback: () =>
                dispatch(openRightModal(<CommercialModal commercial={row} />)),
            },
            {
              text: t("Create the access"),
              hidden: !!accountOf(row),
              callback: () => account(row, "create"),
            },
            {
              text: t("Resend the invitation"),
              hidden: !accountOf(row) || accountOf(row)?.attributes?.blocked,
              callback: () => account(row, "invite"),
            },
            {
              text: accountOf(row)?.attributes?.blocked
                ? t("Enable the access")
                : t("Disable the access"),
              hidden: !accountOf(row),
              callback: () =>
                account(row, accountOf(row)?.attributes?.blocked ? "unblock" : "block"),
            },
            {
              text: t("Delete"),
              red: true,
              loading: deleteLoading,
              callback: () =>
                dispatch(
                  openCenteredModal(
                    <ConfirmModal
                      label={fullName(row)}
                      id={row?.id}
                      onConfirm={() => dispatch(deleteCommercial(row.id, t))}
                    />
                  )
                ),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable
      title="General"
      total={total}
      headers={headers}
      columns={columns}
      data={commercials}
      loading={loading}
      totalPages={pageCount}
      fetchData={fetchData}
      addNewItem={() => dispatch(openRightModal(<CommercialModal />))}
      newItemTitle={t("Add a commercial")}
      noResultsMessage={t("No commercials found.")}
      showRowDetails={false}
    />
  );
};

export default CommercialsTable;
